import type { ProblemTemplate } from '../../types.ts';
import { exactDiv, integerProblem, linear, span, UNKNOWNS } from './helpers-g05-08.ts';

/**
 * Grade 7 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 7: integers arrive — but only to be counted, compared, added and
 * subtracted. Multiplying and dividing integers, and squares, cubes and roots
 * of negatives, are Grade 8, so wherever a Grade 7 problem multiplies,
 * divides or raises to a power, every number involved is positive (checked by
 * the verification tests). Also: algebraic expressions with implied
 * multiplication (4m), substitution, exponents (squares, cubes, cube roots,
 * small powers) and equations with the unknown on both sides whose answer is
 * a positive whole number. Collecting like terms is Grade 8 (see
 * g7.like-terms-mixed in grade08.ts).
 */

const twoStepNeg: ProblemTemplate = {
  id: 'g7.two-step-neg',
  grade: 7,
  title: 'Solve ax − b = c, b − ax = c (negative c) or x + b = c (negative answer)',
  parTimeMs: 14000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = rng.int(2, span(difficulty, 4, 9));
    const magnitude = rng.int(1, span(difficulty, 5, 12));
    const bSpan = span(difficulty, 10, 30);
    const forms = ['ax-b', ...(difficulty >= 0.4 ? ['x+b'] : []), ...(difficulty >= 0.7 ? ['b-ax'] : [])];
    const form = rng.pick(forms);
    // Every form has a negative c or answer — that is the skill being practised — but only integer
    // addition and subtraction is needed: the unknown is positive wherever it is multiplied, so the
    // division that finishes the solution is positive ÷ positive (3x − 15 = −6 → 3x = 9 → x = 3).
    let x: number;
    let prompt: string;
    let distractors: (number | null)[];
    if (form === 'x+b') {
      // x + b = c with c < b: a negative answer by subtracting integers (x + 9 = 4 → x = −5).
      x = -magnitude;
      const b = rng.intExcept(1, Math.max(bSpan, magnitude + 1), [magnitude]); // c ≠ 0
      const c = x + b;
      prompt = rng.chance(0.3) ? `${b} + ${v} = ${c}` : `${v} + ${b} = ${c}`;
      distractors = [
        c + b, // inverse-operation slip: added b instead of subtracting it
        -(c + b), // …and then kept the sign of the bigger number
        -x, // lost the negative sign
      ];
    } else if (form === 'b-ax') {
      // b − ax = c with b < ax, so c is negative (20 − 3x = −4 → x = 8).
      x = magnitude;
      const b = rng.int(1, a * x - 1);
      const c = b - a * x;
      prompt = `${b} - ${a}${v} = ${c}`;
      distractors = [
        -x, // dropped the minus in front of ax when dividing
        exactDiv(b + c, a), // inverse-operation slip on the constant
        b - c, // forgot to divide by a
      ];
    } else {
      // ax − b = c with b > ax, so c is negative (3x − 15 = −6 → x = 3).
      x = magnitude;
      const b = a * x + rng.int(1, bSpan);
      const c = a * x - b;
      prompt = `${a}${v} - ${b} = ${c}`;
      distractors = [
        exactDiv(c - b, a), // inverse-operation slip: subtracted b instead of adding it
        c + b, // added b but forgot to divide by a
        -x, // invented a negative sign because c is negative
      ];
    }
    return integerProblem({
      prompt,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: true,
      distractors,
    });
  },
};

const fracTwoStep: ProblemTemplate = {
  id: 'g7.frac-two-step',
  grade: 7,
  title: 'Solve x/a ± b = c (positive answer, c may be negative)',
  parTimeMs: 14000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const a = rng.int(2, span(difficulty, 4, 10));
    // q = x / a stays positive (a negative x would need integer × and ÷, which is Grade 8);
    // the negative arrives through c = q − b when b > q (x/4 − 15 = −11 → x/4 = 4 → x = 16).
    const q = rng.int(1, span(difficulty, 5, 10));
    const sign = difficulty >= 0.4 && rng.chance(0.5) ? -1 : 1;
    const b = rng.intExcept(1, span(difficulty, 9, 20), [-sign * q]); // c = q ± b ≠ 0
    const c = q + sign * b;
    const x = a * q;
    return integerProblem({
      prompt: `\\frac{${v}}{${a}} ${sign < 0 ? '-' : '+'} ${b} = ${c}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: true,
      distractors: [
        c - sign * b, // undid the ± b but forgot to multiply by a
        a * c - sign * b, // multiplied only c by a, then undid the ± b
        a * (c + sign * b), // inverse-operation slip on the constant
        c < 0 ? -x : null, // carried c's minus sign across to the answer
      ],
    });
  },
};

const substituteNeg: ProblemTemplate = {
  id: 'g7.substitute-neg',
  grade: 7,
  title: 'Substitute with integers: add or subtract a negative value, or get a negative result',
  parTimeMs: 14000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const qMax = span(difficulty, 9, 20);
    const forms = ['add', ...(difficulty >= 0.35 ? ['subtractNeg'] : []), ...(difficulty >= 0.6 ? ['negResult'] : [])];
    const form = rng.pick(forms);
    // Grade 7 integers are only added and subtracted: a negative value is never multiplied or squared.
    let value: number;
    let prompt: string;
    let answer: number;
    let distractors: (number | null)[];
    if (form === 'negResult') {
      // px − q with a positive x and q > px: 3x − 20 with x = 4 → −8 (positive × positive, then subtract).
      const p = rng.int(2, span(difficulty, 5, 9));
      value = rng.int(1, span(difficulty, 3, 6));
      const q = p * value + rng.int(1, qMax);
      prompt = linear([{ coef: p, v }, { coef: -q }]);
      answer = p * value - q;
      distractors = [
        -answer, // took the smaller from the larger and lost the sign
        p * value + q, // sign slip on the constant
        p + value - q, // read "3x" as 3 + x
      ];
    } else if (form === 'subtractNeg') {
      // q − x with a negative x: 12 − x with x = −5 → 17 (subtracting a negative adds).
      const n = rng.int(1, span(difficulty, 3, 9));
      value = -n;
      const q = rng.int(1, qMax);
      prompt = `${q} - ${v}`;
      answer = q + n;
      distractors = [
        q - n, // treated "− (−5)" as "− 5"
        -(q + n), // made the whole answer negative
        n - q, // subtracted the other way round
      ];
    } else {
      // x ± q with a negative x: x + 7 with x = −3 → 4; x − 4 with x = −3 → −7.
      const n = rng.int(1, span(difficulty, 3, 9));
      value = -n;
      const sign = difficulty >= 0.2 && rng.chance(0.4) ? -1 : 1;
      const q = rng.intExcept(1, qMax, sign > 0 ? [n] : []); // never an answer of 0
      const sq = sign * q;
      prompt = sign > 0 && rng.chance(0.3) ? `${q} + ${v}` : linear([{ coef: 1, v }, { coef: sq }]);
      answer = -n + sq;
      distractors = [
        n + sq, // ignored the value's minus sign
        -n - sq, // sign slip on the constant
        -answer, // right size, wrong sign
      ];
    }
    return integerProblem({
      prompt,
      instruction: { key: 'evaluate', params: { assignMath: `${v} = ${value}` } },
      answer,
      allowNegative: true,
      distractors,
    });
  },
};

/**
 * g7.exponent-eval never asks for an answer that is already written in the prompt: an exponent
 * equal to the base ("3^□ = 27") or a cube root of 3 ("□³ = 27") could be answered by copying a
 * number instead of reasoning about repeated multiplication.
 */
const exponentEval: ProblemTemplate = {
  id: 'g7.exponent-eval',
  grade: 7,
  title: 'Exponents: b^□ = c, □³ = c',
  parTimeMs: 10000,
  allowNegative: true,
  generate(rng, difficulty) {
    // Largest power shown: 32 at difficulty 0, 1024 at the top.
    const limit = span(difficulty, 32, 1024);
    // Powers and roots of negatives ((−2)^□, □³ = −8) are CAPS Grade 8, so bases and roots stay positive.
    const forms = ['exponent', ...(difficulty >= 0.5 ? ['cubeRoot'] : [])];
    const form = rng.pick(forms);
    if (form === 'cubeRoot') {
      // □³ = c with c > 0: the integer answer is unique.
      const m = rng.intExcept(2, span(difficulty, 4, 10), [3]);
      const c = m * m * m;
      return integerProblem({
        prompt: `\\box^{3} = ${c}`,
        answer: m,
        allowNegative: true,
        distractors: [
          exactDiv(c, 3), // treated "cubed" as "times 3"
          m + 1, // landed on a neighbouring cube
          m - 1,
        ],
      });
    }
    // b^□ = c with b^e ≤ limit; powers of 10 go up to 10^(2…6).
    const options: { base: number; e: number }[] = [];
    for (let base = 2; base <= 9; base++) {
      for (let e = 2; base ** e <= limit; e++) if (e !== base) options.push({ base, e });
    }
    for (let e = 2; e <= span(difficulty, 2, 6); e++) options.push({ base: 10, e });
    const { base, e } = rng.pick(options);
    const c = base ** e;
    return integerProblem({
      prompt: `${base}^{\\box} = ${c}`,
      answer: e,
      allowNegative: true,
      distractors: [
        exactDiv(c, base), // treated the power as multiplication: b × □ = c
        e + 1, // counted multiplication signs instead of factors (or one too many)
        e - 1,
      ],
    });
  },
};

const bothSidesSimple: ProblemTemplate = {
  id: 'g7.both-sides-simple',
  grade: 7,
  title: 'Solve ax + b = cx + d (positive answer)',
  parTimeMs: 16000,
  allowNegative: true,
  generate(rng, difficulty) {
    const v = rng.pick(UNKNOWNS);
    const coefMax = span(difficulty, 5, 10);
    const big = rng.int(2, coefMax);
    const small = rng.int(1, big - 1);
    const x = rng.int(1, span(difficulty, 6, 12));
    // The constant next to the bigger x-term; negative only in the upper half.
    const bigConst = (difficulty >= 0.5 && rng.chance(0.4) ? -1 : 1) * rng.int(1, span(difficulty, 10, 25));
    const smallConst = (big - small) * x + bigConst;
    const bigSide = linear([{ coef: big, v }, { coef: bigConst }]);
    const smallSide = linear([{ coef: small, v }, { coef: smallConst }]);
    // Upper half: sometimes the bigger x-term sits on the right.
    const swap = difficulty >= 0.6 && rng.chance(0.5);
    return integerProblem({
      prompt: swap ? `${smallSide} = ${bigSide}` : `${bigSide} = ${smallSide}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: true,
      distractors: [
        -x, // moved the constants across without changing their signs
        exactDiv(smallConst + bigConst, big - small), // sign slip on one constant
        exactDiv(smallConst - bigConst, big + small), // added the x-terms instead of subtracting
        smallConst - bigConst, // forgot to divide by the x coefficient
      ],
    });
  },
};

export const GRADE_07: ProblemTemplate[] = [
  twoStepNeg,
  fracTwoStep,
  substituteNeg,
  exponentEval,
  bothSidesSimple,
];
